import { c as _c } from "react/compiler-runtime";
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { startupsPath } from '@src/lib/startups-url';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import type { PaywallGateState } from '../PaywallGate';
import { type PaywallIntentId } from '../intent';
import { conversionForPermission, type ContentPermissionKey } from '../permissions';
import { conversionCheckoutHref } from './checkoutParams';
import { useConversionProducts } from './useConversionProducts';
export type UseConversionGateArgs = {
  /** The grant this gate checks. Also picks the plan and default intent via `conversionForPermission`. */
  permission?: ContentPermissionKey;
  /**
   * Caller-computed unlock for rules wider than one permission (admins, founder-group members,
   * free events, ...). `true` unlocks; otherwise the permission (or registration) check decides,
   * matching `PaywalledAction`.
   */
  isUnlocked?: boolean;
  /** Explicit plan; required when the permission is not sold by Startups Pro or Advisor Team. */
  productId?: string;
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  termId?: string;
};

/**
 * Decides one gate: viewer state, the product that unlocks it, and the handlers that open the
 * Conversion checkout for that same product. Fails closed: while the viewer loads the gate is
 * locked and its CTA disabled; a missing permission read is never treated as entitled.
 */
export function useConversionGate(t0) {
  const $ = _c(36);
  const {
    intent,
    isUnlocked,
    permission,
    productId: explicitProductId,
    termId,
    tokens
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  let t1;
  if ($[0] !== permission) {
    t1 = conversionForPermission(permission);
    $[0] = permission;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const permissionConversion = t1;
  const productId = explicitProductId || permissionConversion?.productId || null;
  const resolvedIntent = intent || permissionConversion?.intent || "default";
  const viewerLoading = Boolean(currentUserLoading) || !currentUser;
  const entitled = !viewerLoading && (isUnlocked === true || (permission ? currentUser?.contentPermissions?.[permission] === true : Boolean(currentUser?.isRegistered)));
  const guest = !viewerLoading && !entitled && Boolean(currentUser?.isGuest);
  let t2;
  if ($[2] !== productId) {
    t2 = [productId];
    $[2] = productId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = termId || "month";
  const t4 = entitled || !productId;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      termId: t3,
      skip: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    products,
    loading: productLoading
  } = useConversionProducts(t2, t5);
  const product = products[0];
  const state = entitled ? "entitled" : guest ? "guest" : "locked";
  const loading = !entitled && (viewerLoading || Boolean(productId) && productLoading);
  let t6;
  if ($[7] !== pathname || $[8] !== productId || $[9] !== resolvedIntent || $[10] !== searchParams || $[11] !== termId || $[12] !== tokens) {
    t6 = productId ? conversionCheckoutHref(pathname || "/", searchParams?.toString(), {
      productId,
      intent: resolvedIntent,
      tokens,
      termId
    }) : null;
    $[7] = pathname;
    $[8] = productId;
    $[9] = resolvedIntent;
    $[10] = searchParams;
    $[11] = termId;
    $[12] = tokens;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const checkoutHref = t6;
  let t7;
  if ($[14] !== checkoutHref || $[15] !== router) {
    t7 = () => {
      if (checkoutHref) {
        router.push(checkoutHref, undefined, {
          scroll: false,
          shallow: true
        });
      }
    };
    $[14] = checkoutHref;
    $[15] = router;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const openCheckout = t7;
  let t8;
  if ($[17] !== checkoutHref || $[18] !== pathname || $[19] !== router) {
    t8 = () => {
      if (checkoutHref) {
        router.push(checkoutHref, undefined, {
          scroll: false,
          shallow: true
        });
      } else {
        router.push(startupsPath(pathname || "/", {
          prompt: "register"
        }), undefined, {
          scroll: false,
          shallow: true
        });
      }
    };
    $[17] = checkoutHref;
    $[18] = pathname;
    $[19] = router;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const onJoin = t8;
  let t9;
  if ($[21] !== pathname || $[22] !== router) {
    t9 = () => {
      router.push(startupsPath(pathname || "/", {
        prompt: "login"
      }), undefined, {
        scroll: false,
        shallow: true
      });
    };
    $[21] = pathname;
    $[22] = router;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const onLogin = t9;
  let t10;
  if ($[24] !== checkoutHref || $[25] !== entitled || $[26] !== loading || $[27] !== onJoin || $[28] !== onLogin || $[29] !== openCheckout || $[30] !== product || $[31] !== productId || $[32] !== resolvedIntent || $[33] !== state || $[34] !== tokens) {
    t10 = {
      state,
      loading,
      entitled,
      product,
      productId,
      intent: resolvedIntent,
      tokens,
      checkoutHref,
      onContinue: openCheckout,
      onJoin,
      onLogin
    };
    $[24] = checkoutHref;
    $[25] = entitled;
    $[26] = loading;
    $[27] = onJoin;
    $[28] = onLogin;
    $[29] = openCheckout;
    $[30] = product;
    $[31] = productId;
    $[32] = resolvedIntent;
    $[33] = state;
    $[34] = tokens;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  return t10;
}